import { HttpClient } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { GoogleLoginResponseOnline } from '@capgo/capacitor-social-login/dist/esm/definitions'
import { Player, PlayerSession } from '@mos/api/player/dto-client'
import { AppConfigService } from '@mos/client/shared/app-config'
import { isServer } from '@mos/client/shared/utils'
import { CheckEmail, Login, Registration, ValidateEmail } from '@mos/shared/types'
import { catchError, Observable, tap } from 'rxjs'
import { AuthRepository } from './auth.repository'

export enum MosSocialProvider {
	GOOGLE = 'GOOGLE',
	FACEBOOK = 'FACEBOOK',
	APPLE = 'APPLE',
}

export interface MosSocialUser {
	provider: MosSocialProvider
	accessToken: string
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	static readonly redirectUrl = '/'
	static readonly loginUrl = '/login'
	static readonly adminLoginUrl = '/login'
	static readonly registerUrl = '/register'
	private readonly httpClient = inject(HttpClient)
	private readonly authRepository = inject(AuthRepository)
	private readonly router = inject(Router)
	private readonly appConfig = inject(AppConfigService)

	authenticationForm?: FormGroup
	authenticationErrorMessage = signal('')

	// SocialLogin.login() throws "Provider was not initialized" if called before this resolves - the native
	// setup isn't instant, and nothing previously stopped a fast tap on the login button from racing it.
	private readonly socialLoginReady: Promise<void> = isServer ? Promise.resolve() : this.initializeSocialLogin()

	private initializeSocialLogin(): Promise<void> {
		const origin = window.location.origin
		return SocialLogin.initialize({
			google: {
				redirectUrl: `${origin}/auth/google`,
				webClientId: this.appConfig.getGoogleClientId().web,
				iOSClientId: this.appConfig.getGoogleClientId().ios,
				mode: 'online',
			},
			apple: {
				clientId: this.appConfig.getAppleClientId(),
				// iOS has native Sign in with Apple support and returns the idToken directly - a
				// non-empty redirectUrl makes the plugin instead POST the code to this URL (meant for
				// Android/web's browser-redirect flow), which on iOS points at the app's own origin
				// and silently fails. Android still needs a non-empty value here even with
				// useBroadcastChannel: true, or its native initialize() rejects and blocks Google too.
				redirectUrl: this.appConfig.getClientApp() === 'ios' ? '' : `${origin}/auth/apple`,
				useProperTokenExchange: true,
				useBroadcastChannel: true,
			},
		})
	}

	private socialLogin(socialUser: MosSocialUser) {
		this.httpClient.post<PlayerSession>('@api/auth/socialLogin', socialUser).subscribe({
			next: (session: PlayerSession) => {
				this.authRepository.setPlayerSession(session)
				SocialLogin.logout({
					provider: socialUser.provider === MosSocialProvider.APPLE ? 'apple' : 'google',
				}).then()
				this.router.navigateByUrl(AuthService.redirectUrl, { onSameUrlNavigation: 'reload' }).then()
			},
			error: (error) => {
				this.authenticationErrorMessage.set(error.error.message)
				this.router.navigateByUrl(AuthService.loginUrl).then()
				throw error
			},
		})
	}

	login(loginCredentials: Login): Observable<PlayerSession> {
		return this.httpClient.post<PlayerSession>('@api/auth/login', loginCredentials).pipe(
			tap((session: PlayerSession) => {
				this.authRepository.setPlayerSession(session)
			}),
			catchError((error) => {
				throw error
			}),
		)
	}

	async loginWithGoogle() {
		await this.socialLoginReady
		const response = await SocialLogin.login({
			provider: 'google',
			options: {
				scopes: ['email'],
				forcePrompt: true,
				forceRefreshToken: true,
			},
		})

		if (!response) {
			throw new Error('Google login failed')
		}

		this.socialLogin({
			provider: MosSocialProvider.GOOGLE,
			accessToken: (response.result as GoogleLoginResponseOnline).idToken ?? '',
		})
	}

	async loginWithApple() {
		await this.socialLoginReady
		const response = await SocialLogin.login({
			provider: 'apple',
			options: {
				scopes: ['email'],
			},
		})

		if (!response) {
			throw new Error('Apple login failed')
		}

		this.socialLogin({
			provider: MosSocialProvider.APPLE,
			accessToken: response.result.idToken ?? '',
		})
	}

	async logout() {
		this.authRepository.removePlayer()
		return this.router.navigateByUrl('/', { onSameUrlNavigation: 'reload' })
	}

	getPlayer(): Observable<Player> {
		return this.httpClient.get<Player>('@api/auth')
	}

	sendRegistrationCode(data: CheckEmail) {
		return this.httpClient.post('@api/auth/sendRegistrationCode', data)
	}

	sendUpdatePasswordCode(data: CheckEmail) {
		return this.httpClient.post('@api/auth/sendUpdatePasswordCode', data)
	}

	checkCode(data: ValidateEmail) {
		return this.httpClient.post('@api/auth/checkCode', data).pipe(
			catchError((error) => {
				this.authenticationErrorMessage.set(error.error.message)
				throw error
			}),
		)
	}

	register(data: Registration) {
		return this.httpClient.post<PlayerSession>('@api/auth/register', data).pipe(
			tap((session) => {
				this.authRepository.setPlayerSession(session)
			}),
			catchError((error) => {
				this.authenticationErrorMessage.set(error.error.message)
				throw error
			}),
		)
	}

	updatePassword(data: Registration) {
		return this.httpClient.post<PlayerSession>('@api/auth/updatePassword', data).pipe(
			tap((session) => {
				this.authRepository.setPlayerSession(session)
			}),
			catchError((error) => {
				this.authenticationErrorMessage.set(error.error.message)
				throw error
			}),
		)
	}

	acceptTos() {
		return this.httpClient.post(`@api/account/acceptTos`, {})
	}

	deleteAccount() {
		return this.httpClient.delete(`@api/account`, {})
	}

	cancelAccountDelete() {
		return this.httpClient.post(`@api/account/cancelDelete`, {})
	}
}
